import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/SEO";
import { Link } from "react-router-dom";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { MessageSquare, Clock, CheckSquare, Users, ArrowRight, Sparkles, Heart, Play, HelpCircle, Briefcase, Coffee, Timer, LayoutGrid } from "lucide-react";

const faqData = [
  {
    question: "What happened to HipChat?",
    answer: "HipChat was discontinued on February 15, 2019 after Atlassian partnered with Slack. CSW (Chill Study Work) is now a free alternative offering real-time collaboration with Study Stream, Study Timer, and Task Management features."
  },
  {
    question: "Is CSW a free HipChat alternative?",
    answer: "Yes, CSW is completely free to use with no limits. You can join unlimited study and work sessions, use collaboration tools, and manage tasks without any subscription fees."
  },
  {
    question: "What features does CSW offer compared to HipChat?",
    answer: "CSW offers 24/7 virtual rooms for real-time collaboration, customizable Study Timer with Pomodoro technique, Task Management with Kanban boards, and community features—all free."
  },
  {
    question: "Can I collaborate with teams on CSW like HipChat?",
    answer: "Absolutely! CSW's Study Stream feature lets you join or host live collaboration sessions with people worldwide, creating accountability and teamwork that HipChat offered."
  },
  {
    question: "Does CSW have chat features like HipChat?",
    answer: "Yes, CSW has real-time chat in study rooms, allowing you to communicate with teammates while working together on tasks."
  },
  {
    question: "Is CSW better than HipChat was?",
    answer: "CSW offers different features focused on productivity—including unlimited 24/7 streams, task management, timers, and themed study rooms—all completely free."
  }
];

const HipChat = () => {
  return (
    <div className="min-h-screen bg-background">
      <SEO 
        title="HipChat Alternative - Free Team Collaboration"
        description="Looking for a HipChat alternative? HipChat shut down in 2019 but CSW offers free team collaboration with Study Stream, Study Timer, and Task Management. Start collaborating today."
        keywords="hipchat alternative, hipchat app, team chat, slack alternative, team collaboration, remote work productivity"
        canonicalUrl="/hipchat/"
      />
      
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "WebPage",
        "name": "HipChat Alternative - Free Team Collaboration | CSW",
        "description": "Looking for a HipChat alternative? HipChat shut down in 2019 but CSW offers free team collaboration with Study Stream, Study Timer, and Task Management.",
        "url": "https://csw.live/hipchat/",
        "inLanguage": "en",
        "isPartOf": {
          "@type": "WebSite",
          "name": "CSW - Chill Study Work",
          "url": "https://csw.live"
        },
        "about": { "@type": "Thing", "name": "HipChat Alternative" },
        "mentions": [
          { "@type": "Thing", "name": "HipChat" },
          { "@type": "Thing", "name": "Team Chat" },
          { "@type": "Thing", "name": "Slack Alternative" }
        ],
        "keywords": "hipchat alternative, hipchat app, team chat, slack alternative, team collaboration"
      })}} />
      
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "SoftwareApplication",
        "name": "CSW - HipChat Alternative",
        "applicationCategory": "ProductivityApplication",
        "operatingSystem": "Web Browser",
        "description": "Free HipChat alternative with virtual collaboration rooms, Study Timer, and Task Management.",
        "url": "https://csw.live/hipchat/",
        "offers": { "@type": "Offer", "price": "0", "priceCurrency": "USD" },
        "aggregateRating": { "@type": "AggregateRating", "ratingValue": "5", "ratingCount": "1000", "bestRating": "5", "worstRating": "1" },
        "featureList": ["Free Team Collaboration", "24/7 Study Rooms", "Real-time Chat", "Task Management", "Pomodoro Timer", "No Subscription Required", "Global Community", "File Sharing"],
        "publisher": { "@type": "Organization", "name": "CSW - Chill Study Work", "url": "https://csw.live" }
      })}} />
      
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": faqData.map(faq => ({
          "@type": "Question",
          "name": faq.question,
          "acceptedAnswer": { "@type": "Answer", "text": faq.answer }
        }))
      })}} />
      
      <Header />

      <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-background via-muted to-background" />
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/20 rounded-full blur-[120px] animate-glow-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-secondary/20 rounded-full blur-[120px] animate-glow-pulse" style={{ animationDelay: '1s' }} />
        
        <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="max-w-5xl mx-auto text-center space-y-8">
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-bold leading-tight animate-fade-in">
              <span className="text-gradient">HipChat</span>
            </h1>
            
            <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight animate-fade-in" style={{ animationDelay: '0.1s' }}>
              HipChat Alternative
            </h1>
            
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-semibold leading-tight animate-fade-in text-muted-foreground" style={{ animationDelay: '0.2s' }}>
              Free Team Collaboration{" "}
              <span className="text-gradient">24/7</span>
            </h2>
            
            <p className="text-xl sm:text-2xl text-muted-foreground max-w-3xl mx-auto font-light animate-fade-in" style={{ animationDelay: '0.2s' }}>
              When HipChat shut down in 2019, teams lost a beloved collaboration tool. CSW fills that void with free, unlimited collaboration sessions—no subscription required. Join our global community of <Link to="/study-with-me/" className="text-primary hover:underline">productive learners and workers</Link>.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center pt-4 animate-fade-in" style={{ animationDelay: '0.3s' }}>
              <Button size="lg" className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-8 py-6 text-lg glow-primary transition-all hover:scale-105" asChild>
                <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer">
                  Start Collaborating Free <ArrowRight className="ml-2 w-5 h-5" />
                </a>
              </Button>
              <div className="flex items-center gap-6 text-sm">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Heart className="w-4 h-4 text-primary fill-primary" />
                  <span className="font-medium">Free Forever</span>
                </div>
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Sparkles className="w-4 h-4 text-primary" />
                  <span className="font-medium">No Limits</span>
                </div>
              </div>
            </div>
            
            <div className="relative mt-12 animate-fade-in" style={{ animationDelay: '0.4s' }}>
              <div className="relative rounded-2xl overflow-hidden border-2 border-border/50 bg-card/30 backdrop-blur-sm shadow-2xl">
                <video autoPlay loop muted playsInline className="w-full aspect-video object-cover">
                  <source src="/videos/productivity-demo.mp4" type="video/mp4" />
                </video>
                <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent" />
                <div className="absolute bottom-6 left-6 flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-primary/90 flex items-center justify-center glow-primary">
                    <Play className="w-5 h-5 text-primary-foreground ml-1" />
                  </div>
                  <span className="text-foreground font-medium">See CSW in action</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-muted/50 to-background" />
        <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="max-w-4xl mx-auto p-8 sm:p-12 bg-card/50 backdrop-blur-sm border-primary/20">
            <div className="space-y-6">
              <div className="flex items-start gap-6">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center flex-shrink-0">
                  <MessageSquare className="w-7 h-7 text-primary" />
                </div>
                <div className="space-y-4">
                  <h2 className="font-display text-3xl sm:text-4xl font-bold text-foreground">
                    HipChat Has <span className="text-gradient">Shut Down</span>
                  </h2>
                  <p className="text-muted-foreground text-lg leading-relaxed">
                    HipChat—Atlassian's team chat app that pioneered workplace messaging—was discontinued on February 15, 2019 after Atlassian partnered with Slack. Despite serving millions of teams worldwide, the platform couldn't compete with Slack's momentum. <Link to="/hipchat/history/" className="text-primary hover:underline font-medium">Read the full HipChat story →</Link>
                  </p>
                  <p className="text-muted-foreground text-lg leading-relaxed">
                    <span className="font-semibold text-foreground">CSW – Chill Study Work</span> is here to fill the void with a free, feature-rich alternative. Explore our <Link to="/online-workspace/" className="text-primary hover:underline">Online Workspace</Link>, <Link to="/productivity-tool/" className="text-primary hover:underline">Productivity Tools</Link>, and <Link to="/study-with-friends/" className="text-primary hover:underline">collaborative modes</Link>.
                  </p>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </section>

      <section className="py-24 relative overflow-hidden">
        <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            <div className="space-y-6">
              <h2 className="font-display text-4xl sm:text-5xl font-bold text-foreground">
                24/7 <span className="text-gradient">Collaboration Rooms</span>
              </h2>
              <p className="text-muted-foreground text-lg leading-relaxed">
                CSW's <Link to="/study-stream/" className="text-primary hover:underline">study rooms</Link> are always open. Jump in anytime and work alongside peers from around the world.
              </p>
              <p className="text-muted-foreground text-lg leading-relaxed">
                CSW operates 24/7 with no caps on session duration. Join our <Link to="/group-study/" className="text-primary hover:underline">group study sessions</Link> to experience collaborative productivity.
              </p>
            </div>
            <Card className="p-8 bg-card/50 backdrop-blur-sm border-border/50">
              <div className="aspect-video bg-gradient-to-br from-primary/10 to-secondary/10 rounded-xl flex items-center justify-center">
                <Users className="w-16 h-16 text-muted-foreground/50" />
              </div>
            </Card>
          </div>
        </div>
      </section>

      <section className="py-24 relative overflow-hidden">
        <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            <Card className="p-8 bg-gradient-to-br from-primary/10 to-secondary/10 border-primary/20">
              <div className="aspect-video bg-muted/50 rounded-xl flex items-center justify-center">
                <Timer className="w-16 h-16 text-muted-foreground/50" />
              </div>
            </Card>
            <div className="space-y-6">
              <h2 className="font-display text-4xl sm:text-5xl font-bold text-foreground">
                Task <span className="text-gradient">Management</span>
              </h2>
              <p className="text-muted-foreground text-lg leading-relaxed">
                CSW's <Link to="/task-management/" className="text-primary hover:underline">task management</Link> tools help you stay organized with Kanban boards, to-do lists, and <Link to="/reminders/" className="text-primary hover:underline">reminders</Link>.
              </p>
              <p className="text-muted-foreground text-lg leading-relaxed">
                Learn about <Link to="/blog/apps-boost-organization-college/" className="text-primary hover:underline">apps to boost organization</Link>.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-8">
              <HelpCircle className="w-8 h-8 text-primary" />
              <h2 className="font-display text-3xl sm:text-4xl font-bold">
                Frequently Asked <span className="text-gradient">Questions</span>
              </h2>
            </div>
            <Accordion type="single" collapsible className="space-y-4">
              {faqData.map((faq, index) => (
                <AccordionItem key={index} value={`item-${index}`} className="bg-card rounded-lg border border-border/50 px-6">
                  <AccordionTrigger className="text-left font-medium py-4">{faq.question}</AccordionTrigger>
                  <AccordionContent className="text-muted-foreground pb-4">{faq.answer}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-background to-secondary/10" />
        <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center space-y-8">
            <h2 className="font-display text-4xl sm:text-5xl font-bold">
              Ready to <span className="text-gradient">Get Started?</span>
            </h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Join thousands who've made the switch to CSW—a free, stable platform built to last.
            </p>
            <Button size="lg" className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-8 py-6 text-lg" asChild>
              <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer">
                Start Free Today <ArrowRight className="ml-2 w-5 h-5" />
              </a>
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HipChat;
